<script lang="ts">
	import ImageOptimiser from '$lib/components/common/ImageOptimiser.svelte';
	import { onMount, onDestroy } from 'svelte';
	import * as PARTNERS from '$ravioli/lib/assets/images/partners';
	import scrollRight from '$ravioli/lib/assets/icons/scroll-arrow-right.svg';
	import scrollLeft from '$ravioli/lib/assets/icons/scroll-arrow-left.svg';
	
	let sliderContainer: HTMLElement;
	let activeDot = 0;
	let canScrollLeft = false;
	let canScrollRight = true;
	const totalDots = 4;

	let partnerLogo = [
		{ image: PARTNERS.beyondOrganic },
		{ image: PARTNERS.cocoStory },
		{ image: PARTNERS.f45Training },
		{ image: PARTNERS.metroMedi },
		{ image: PARTNERS.nColdpressed },
		{ image: PARTNERS.simplyFresh },
		{ image: PARTNERS.vijaynagarFood }
	];

	const CARD_WIDTH = 300; // md:max-w-[300px]
	const GAP = 40; // md:gap-10 (40px)

	const scroll = (direction: 'left' | 'right') => {
		if (!sliderContainer) return;

		const scrollAmount = CARD_WIDTH + GAP;
		const scrollPosition =
			direction === 'left'
				? sliderContainer.scrollLeft - scrollAmount
				: sliderContainer.scrollLeft + scrollAmount;

		sliderContainer.scrollTo({
			left: scrollPosition,
			behavior: 'smooth'
		});
	};

	const updateScrollState = () => {
		if (sliderContainer) {
			const { scrollLeft, scrollWidth, clientWidth } = sliderContainer;

			// Update active dot based on scroll percentage
			const maxScroll = scrollWidth - clientWidth;
			const scrollPercentage = scrollLeft / maxScroll;
			activeDot = Math.min(Math.floor(scrollPercentage * totalDots), totalDots - 1);

			// Check scroll capabilities
			canScrollLeft = scrollLeft > 0;
			canScrollRight = scrollLeft + clientWidth < scrollWidth;
		}
	};

	onMount(() => {
		if (sliderContainer) {
			sliderContainer.addEventListener('scroll', updateScrollState);
			window.addEventListener('resize', updateScrollState);
		}
	});

	onDestroy(() => {
		if (sliderContainer) {
			sliderContainer.removeEventListener('scroll', updateScrollState);
			window.removeEventListener('resize', updateScrollState);
		}
	});
</script>

<section class="font-roboto relative flex flex-col gap-8 overflow-hidden px-4 py-11 md:py-12">
	<div class="flex flex-col gap-5 md:gap-7">
		<p class="title-underline relative mx-auto max-w-fit text-xl font-medium uppercase md:text-2xl">
			Our Partners
		</p>
	</div>
	<div class="relative mx-auto md:max-w-[1120px]">
		<div class="absolute top-[50%] w-screen lg:w-full">
			<!-- Right Navigation Button -->
			<!-- svelte-ignore a11y_no_static_element_interactions -->
			<!-- svelte-ignore a11y_click_events_have_key_events -->
			<div
				class=" swiper-button-next right-0 mr-4 !hidden md:!flex lg:mr-0 !w-10 !h-10"
				class:disabled={!canScrollRight}
				style:opacity={canScrollRight ? '1' : '0.3'}
				on:click={() => canScrollRight && scroll('right')}
			>
		<img class="w-full h-full object-cover" src="{scrollRight}" alt=" right"></div>

			<!-- Left Navigation Button -->
			<!-- svelte-ignore a11y_no_static_element_interactions -->
			<!-- svelte-ignore a11y_click_events_have_key_events -->
			<div
				class=" swiper-button-prev !-left-5 !hidden md:!flex !w-10 !h-10"
				class:disabled={!canScrollLeft}
				style:opacity={canScrollLeft ? '1' : '0.3'}
				on:click={() => canScrollLeft && scroll('left')}
			><img class="w-full h-full object-cover" src="{scrollLeft}" alt="scroll left">
		</div>
		</div>

		<div
			bind:this={sliderContainer}
			class="hide-scrollbar flex w-full cursor-pointer flex-row flex-nowrap gap-5 overflow-x-auto px-4 py-3 md:gap-10 md:px-8"
		>
			{#each partnerLogo as partner}
				<li
					class="flex   max-w-[152px] flex-none flex-col overflow-hidden rounded-lg shadow-[0px_2px_8px_0px_rgba(0,_0,_0,_0.08)] md:max-w-[300px] md:rounded-2xl"
				>
					<ImageOptimiser
						src={partner.image}
						alt="partner logo"
						imageSourceType="internal"
						classes={{ image: 'w-[120px] h-[120px] md:w-[250px] md:h-[250px] object-center object-cover' }}
					/>
				</li>
			{/each}
		</div>

		<div class="pagination-dots mt-5 flex justify-center md:mt-10">
			{#each Array(totalDots) as _, i}
				<!-- svelte-ignore a11y_no_static_element_interactions -->
				<!-- svelte-ignore a11y_click_events_have_key_events -->
				<div
					class="dot"
					class:active={activeDot === i}
					on:click={() => {
						if (!sliderContainer) return;
						const { scrollWidth, clientWidth } = sliderContainer;
						const maxScroll = scrollWidth - clientWidth;
						const scrollPosition = (i / (totalDots - 1)) * maxScroll;
						sliderContainer.scrollTo({
							left: scrollPosition,
							behavior: 'smooth'
						});
					}}
				></div>
			{/each}
		</div>
	</div>
</section>
